import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Stack,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import {
  editPatientButton,
  registerPatientButtonStyles,
  DeletePatientButton,
  addButtonPadding,
} from "../../../common/commonStyles";
import DeleteIcon from "@mui/icons-material/Delete";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import validators from "../../../utils/validations/validators";
import api from "../../../services/api";
import "./SlotassignDialog.css";

export default function SlotassignDialog({
  open,
  onClose,
  handleSlotSubmit,
  serviceDetails,
  therapyPlansData,
  handlePatientSlotDelete,
  handlePatientSlotEdit,
  branchId,
}) {
  const [formData, setFormData] = useState({});
  const [isEdit, setEdit] = useState(false);
  const [recordID, setRecordID] = useState();
  const [validationErrors, setValidationErrors] = useState({});
  const [slots, setSlots] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceClicked, setServiceClicked] = useState(false);

  const transformedData = therapyPlansData.map((patient) => ({
    ...patient,
    services: Object.values(patient.services),
  }));
  console.log("Transformed Therapy Plans Data:", therapyPlansData);

  const handleTimeslotChange = useCallback(
    async (id) => {
      let timeSlots = await api.get(
        `app/slots/?branch_id=${branchId}&service_id=${id}`,
      );
      if (timeSlots?.status === 200) {
        setSlots(timeSlots?.data);
      }
    },
    [branchId],
  );

  // Map day_of_week (0-6) to day names
  const dayOfWeekMap = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };

  // Transform schedules array to therapy_days and service_slot_ids format
  const transformSchedulesToFormFormat = (schedules) => {
    const therapy_days = [];
    const service_slot_ids = {};

    if (schedules && Array.isArray(schedules)) {
      schedules.forEach((schedule) => {
        const dayName = dayOfWeekMap[schedule.day_of_week];
        therapy_days.push(dayName);
        service_slot_ids[dayName] = String(schedule.service_slot_id);
      });
    }

    return { therapy_days, service_slot_ids };
  };
  useEffect(() => {
    if (!open || !therapyPlansData?.length) return;

    // avoid resetting if already filled
    if (Object.keys(formData).length > 0) return;

    const patient = therapyPlansData[0];
    const serviceObj = Object.values(patient.services || {})[0];
    if (!serviceObj) return;

    const { therapy_days, service_slot_ids } = transformSchedulesToFormFormat(
      serviceObj.schedules,
    );

    const matchedService = serviceDetails.find(
      (s) => s.name === serviceObj.service_name,
    );

    if (!matchedService) return;

    setFormData({
      service_id: matchedService.id,
      total_sessions: serviceObj.total_sessions,
      frequency_per_week: serviceObj.frequency_per_week,
      therapy_days,
      service_slot_ids,
      is_slot_allocated: true,
    });

    setSelectedService(matchedService);
    setServiceClicked(true);
  }, [open, therapyPlansData, serviceDetails]);
  console.log("Form Data on Load/Edit:", formData);

  useEffect(() => {
    if (branchId && formData?.service_id) {
      handleTimeslotChange(formData?.service_id);
    }
  }, [formData?.service_id, branchId, handleTimeslotChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let parsedValue = value;

    // Only split value into array if it is from a multi-select
    if (Array.isArray(value)) {
      parsedValue = value; // already an array (Select with `multiple`)
    } else if (name === "therapy_days") {
      parsedValue = value.split(","); // fallback if manually added comma-separated value
    } else if (!isNaN(value) && name !== "service_id") {
      parsedValue = Number(value); // convert numeric strings to numbers
    }

    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
      is_slot_allocated: true, // if you want this flag
    }));

    // Validate live
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  const handleDayCardClick = (day) => {
    setFormData((prev) => {
      const updatedDays = prev.therapy_days?.includes(day)
        ? prev.therapy_days.filter((d) => d !== day)
        : [...(prev.therapy_days || []), day];
      return {
        ...prev,
        therapy_days: updatedDays,
        is_slot_allocated: true,
      };
    });
  };

  const requiredFields = [
    "service_id",
    "total_sessions",
    "frequency_per_week",
    "therapy_days",
  ];

  const isFormValid =
    Object.values(validationErrors).every((err) => err === "") &&
    requiredFields.every((field) => {
      const val = formData[field];
      if (Array.isArray(val)) return val.length > 0;
      return val !== "" && val !== null && val !== undefined;
    }) &&
    // check every selected day has a service_slot_id chosen
    formData?.therapy_days?.length > 0 &&
    formData.therapy_days.every(
      (day) =>
        formData?.service_slot_ids?.[day] !== undefined &&
        formData?.service_slot_ids?.[day] !== "",
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Build schedules array from therapy_days and service_slot_ids
    const dayMap = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6
    };

    const schedules =
      formData?.therapy_days?.map((day) => ({
        day_of_week: dayMap[day],
        service_slot_id: parseInt(formData.service_slot_ids[day]),
      })) || [];

    const formattedData = {
      service_id: formData.service_id,
      total_sessions: formData.total_sessions,
      frequency_per_week: formData.frequency_per_week,
      schedules: schedules,
      is_slot_allocated: true,
    };
    if (isEdit) {
      await handlePatientSlotEdit(formattedData, recordID);
    } else {
      await handleSlotSubmit(formattedData);
    }
    setFormData({});
  };

  const handleslotDelete = async (id) => {
    handlePatientSlotDelete(id);
  };

  const handleslotEdit = async (id, serviceName) => {
    setEdit(true);
    setRecordID(id);
    const filtered = transformedData
      .map((patient) => ({
        ...patient,
        services: patient.services.filter(
          (service) => service.record_id === id,
        ),
      }))
      .filter((patient) => patient.services.length > 0);

    const transformedfiltered = filtered.flatMap((patient) =>
      patient.services.map((service) => {
        // Transform schedules to therapy_days and service_slot_ids
        const { therapy_days, service_slot_ids } =
          transformSchedulesToFormFormat(service.schedules);

        return {
          service_name: service.service_name,
          record_id: service.record_id,
          is_slot_allocated: service.session_id !== null,
          total_sessions: service.total_sessions,
          frequency_per_week: service.frequency_per_week || 0,
          time_slot: service.time_slot || "",
          therapy_days: therapy_days,
          service_slot_ids: service_slot_ids,
        };
      }),
    );

    const matchedService = serviceDetails.find(
      (service) => service.name === serviceName,
    );

    // Rename `id` to `service_id` and flatten
    const { id: service_id, ...restService } = matchedService || {};
    const flattened = {
      ...transformedfiltered[0],
      ...restService,
      service_id, // renamed from id
    };
    setFormData(flattened);
    setSelectedService(matchedService);
    setServiceClicked(true);
  };

  const allDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const columns = [
    {
      field: "service_name",
      headerName: "Service Name",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "frequency_per_week",
      headerName: "Frequency Per Week",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "total_sessions",
      headerName: "Total Session",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "therapy_days",
      headerName: "Therapy Days",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "time_slot",
      headerName: "Time Slot",
      flex: 0.8,
      minWidth: 110,
      editable: false,
    },
    {
      field: "",
      headerName: "Action",
      width: 150,
      editable: false,
      sortable: false,
      renderCell: (params) => (
        <Box className="center-align">
          <DeleteIcon
            sx={DeletePatientButton}
            onClick={() => {
              handleslotDelete(params.row.record_id);
            }}
          />
          <BorderColorIcon
            sx={editPatientButton}
            onClick={() => {
              handleslotEdit(params.row.record_id, params.row.service_name);
            }}
          />
        </Box>
      ),
    },
  ];

const handleServiceSelect = (id, name) => {
  const selectedService = serviceDetails.find((s) => s.id === id);

  // find existing data for this service
  const patient = therapyPlansData?.[0];
  const serviceObj = patient?.services?.[name]; // KEY POINT

  if (serviceObj) {
    // ✅ EXISTING DATA → PREFILL
    const { therapy_days, service_slot_ids } =
      transformSchedulesToFormFormat(serviceObj.schedules);

    setFormData({
      service_id: selectedService.id,
      total_sessions: serviceObj.total_sessions,
      frequency_per_week: serviceObj.frequency_per_week,
      therapy_days,
      service_slot_ids,
      is_slot_allocated: true,
    });
  } else {
    // ❌ NO DATA → EMPTY FORM (CREATE)
    setFormData({
      service_id: selectedService.id,
      total_sessions: "",
      frequency_per_week: "",
      therapy_days: [],
      service_slot_ids: {},
      is_slot_allocated: true,
    });
  }

  setSelectedService(selectedService);
  setServiceClicked(true);
};
  return (
    <Dialog
      open={open}
      onClose={() => {
        setFormData({});
        setValidationErrors({});
        setEdit(false);
        setRecordID(null);
        onClose();
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle className="">Assign Therapy To Patient</DialogTitle>
      <DialogContent>
        <Box>
          <>
            <Grid
              container
              spacing={4}
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "center",
              }}
            >
              <Grid size={3}>
                <Stack spacing={2}>
                  {serviceDetails?.map((step) => (
                    <div
                      key={step.id}
                      className={`service-item ${
                        selectedService?.id === step.id ? "selected" : ""
                      }`}
                      onClick={() => handleServiceSelect(step.id, step?.name)}
                    >
                      {step?.name}
                    </div>
                  ))}
                </Stack>
              </Grid>
              <Grid size={9}>
                <>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Grid size={12}>
                      <TextField
                        name="total_sessions"
                        label="Total Sessions"
                        value={formData?.total_sessions || ""}
                        onChange={handleChange}
                        fullWidth
                        margin="dense"
                        variant="standard"
                        className="custom-textfield"
                        error={!!validationErrors.total_sessions}
                        helperText={validationErrors.total_sessions}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        name="frequency_per_week"
                        label="Frequency Per Week"
                        value={formData?.frequency_per_week || ""}
                        onChange={handleChange}
                        fullWidth
                        margin="dense"
                        variant="standard"
                        className="custom-textfield"
                        error={!!validationErrors.frequency_per_week}
                        helperText={validationErrors.frequency_per_week}
                      />
                    </Grid>

                    {/* Therapy Days - 7 Cards in Row */}
                    <Grid size={12}>
                      <Box sx={{ mb: 3 }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.9rem",
                            fontWeight: 500,
                            marginBottom: "16px",
                            color: "#333",
                          }}
                        >
                          Therapy Days
                        </label>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 1fr)",
                            gap: "12px",
                            mb: 2,
                          }}
                        >
                          {allDays.map((day) => (
                            <Box
                              key={`day-${day}`}
                              onClick={() => handleDayCardClick(day)}
                              sx={{
                                padding: "14px 12px",
                                textAlign: "center",
                                cursor: "pointer",
                                borderRadius: "8px",
                                border: "2px solid #ddd",
                                transition: "all 0.3s ease",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                backgroundColor:
                                  formData?.therapy_days?.includes(day)
                                    ? "#4747a1"
                                    : "#fff",
                                color: formData?.therapy_days?.includes(day)
                                  ? "#fff"
                                  : "#666",
                                borderColor: formData?.therapy_days?.includes(
                                  day,
                                )
                                  ? "#4747a1"
                                  : "#ddd",
                                "&:hover": {
                                  borderColor: "#4747a1",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              {day.slice(0, 3)}
                            </Box>
                          ))}
                        </Box>
                        {validationErrors.therapy_days && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "0.8rem",
                              margin: "0 0 16px 0",
                            }}
                          >
                            {validationErrors.therapy_days}
                          </p>
                        )}
                      </Box>
                    </Grid>

                    {/* Time Slot Dropdowns - 7 in Row */}
                    <Grid size={12}>
                      <Box sx={{ mt: 2 }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.9rem",
                            fontWeight: 500,
                            marginBottom: "16px",
                            color: "#333",
                          }}
                        >
                          Time Slots
                        </label>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 1fr)",
                            gap: "12px",
                          }}
                        >
                          {allDays.map((day) => {
                            const isDaySelected =
                              formData?.therapy_days?.includes(day);
                            return (
                              <TextField
                                key={`slot-${day}`}
                                select
                                className="custom-textfield"
                                name={`service_slot_id_${day}`}
                                value={formData?.service_slot_ids?.[day] || ""}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    service_slot_ids: {
                                      ...prev.service_slot_ids,
                                      [day]: e.target.value,
                                    },
                                  }));
                                }}
                                fullWidth
                                variant="outlined"
                                size="small"
                                disabled={!isDaySelected}
                                placeholder="Select"
                                sx={{
                                  opacity: isDaySelected ? 1 : 0.5,
                                  transition: "all 0.3s ease",
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: isDaySelected
                                      ? "#fff"
                                      : "#f5f5f5",
                                    fontSize: "0.85rem",
                                  },
                                }}
                              >
                                {slots.map((slot) => (
                                  <MenuItem
                                    key={slot.slot_id}
                                    value={String(slot.slot_id)}
                                  >
                                    {slot.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            );
                          })}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              </Grid>
            </Grid>
          </>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          sx={[registerPatientButtonStyles, addButtonPadding]}
          size="small"
          onClick={handleSubmit}
          disabled={!isFormValid}
        >
          {isEdit ? "Save" : "Save"}
        </Button>
        <Button
          size="small"
          onClick={() => {
            setFormData({});
            setValidationErrors({});
            setEdit(false);
            setRecordID(null);
            setSelectedService(null);
            setServiceClicked(false);
            onClose();
          }}
          type="button"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
